import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Button } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

// Captcha (Cloudflare Turnstile) en un iframe servido desde secure.pxsol.com
// (public/captcha.html): así el widget sólo necesita ese hostname aunque el
// login esté en el dominio de cada hotel. Protocolo de mensajes:
//   iframe -> padre: pxcaptcha:token {token, action} | pxcaptcha:expired |
//                    pxcaptcha:error {code} | pxcaptcha:resize {height}
//   padre -> iframe: pxcaptcha:reset (los tokens son de un solo uso)

// null si el captcha no está configurado: en ese caso no se renderiza nada y
// no se manda token (compatible con api2 en modo `off`).
export const getCaptchaConfig = () => {
  const frameUrl = process.env.REACT_APP_CAPTCHA_FRAME_URL;
  const sitekey = process.env.REACT_APP_TURNSTILE_SITEKEY;
  if (!frameUrl || !sitekey) return null;
  try {
    const url = new URL(frameUrl);
    const local = ["localhost", "127.0.0.1"].indexOf(url.hostname) !== -1;
    if (url.protocol !== "https:" && !local && !warnedInsecure) {
      warnedInsecure = true;
      console.warn(
        "REACT_APP_CAPTCHA_FRAME_URL no es https: el captcha debe servirse por HTTPS"
      );
    }
    return { frameUrl, sitekey, origin: url.origin };
  } catch (e) {
    return null;
  }
};
let warnedInsecure = false;

export const buildCaptchaSrc = ({ frameUrl, sitekey }, { action, pos, lng }) => {
  const url = new URL(frameUrl);
  url.searchParams.set("sitekey", sitekey);
  url.searchParams.set("action", action);
  if (pos) url.searchParams.set("pos", pos);
  if (lng) url.searchParams.set("lng", lng);
  return url.toString();
};

// Sin ningún mensaje del iframe en este tiempo se considera caído.
export const LOAD_TIMEOUT_MS = 10000;
const DEFAULT_HEIGHT = 70;
const MAX_HEIGHT = 500;

// Motivo (código corto, lista cerrada) con el que el login sale SIN
// captcha_token: viaja a api2 como `captcha_fallback`. `code` es lo que llega
// por onError: "timeout" (el iframe no respondió), los códigos de
// public/captcha.html o los de error de Turnstile.
export const captchaFallbackReason = code => {
  if (code === "script_load_timeout" || code === "script_load_failed")
    return code;
  if (code === "timeout") return "widget_timeout";
  if (typeof code === "string" && code) return "widget_error";
  return "unknown";
};

const CaptchaFrame = ({
  action,
  pos,
  lng,
  onToken,
  onError,
  resetKey,
  canContinue
}) => {
  const intl = useIntl();
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  // "loading" | "ready" | "error"; `attempt` recarga el iframe (Reintentar).
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const watchdog = useRef(null);
  // Antes de avisar un fallo (onError, que el padre cuenta) se recarga el
  // iframe UNA vez sola: en celulares una carga lenta suele resolverse en el
  // segundo intento. Se rearma al recibir un token o al tocar "Reintentar".
  const autoRetried = useRef(false);
  const config = useMemo(getCaptchaConfig, []);
  const handlers = useRef({ onToken, onError });
  handlers.current = { onToken, onError };

  const clearWatchdog = () => {
    if (watchdog.current) clearTimeout(watchdog.current);
    watchdog.current = null;
  };

  // El iframe nuevo (otra `key`) tiene otro contentWindow: lo que mande el
  // anterior, token incluido, se descarta en el chequeo de `event.source`.
  const fail = code => {
    clearWatchdog();
    if (!autoRetried.current) {
      autoRetried.current = true;
      setStatus("loading");
      setAttempt(n => n + 1);
      return;
    }
    setStatus("error");
    if (handlers.current.onError) handlers.current.onError(code);
  };

  // Watchdog: si en LOAD_TIMEOUT_MS el iframe no manda ningún mensaje (script
  // bloqueado, red caída, X-Frame-Options...) se reintenta o pasa a error.
  useEffect(() => {
    if (!config) return undefined;
    setStatus("loading");
    clearWatchdog();
    watchdog.current = setTimeout(() => fail("timeout"), LOAD_TIMEOUT_MS);
    return clearWatchdog;
  }, [config, attempt]);

  useEffect(() => {
    if (!config) return undefined;
    const onMessage = event => {
      // Sólo mensajes del origen configurado Y de NUESTRO iframe.
      if (event.origin !== config.origin) return;
      const frame = iframeRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== "object" || typeof data.type !== "string")
        return;
      if (data.type.indexOf("pxcaptcha:") === 0) clearWatchdog();
      switch (data.type) {
        case "pxcaptcha:token":
          setStatus("ready");
          if (typeof data.token !== "string" || !data.token) return;
          if (data.action && data.action !== action) return;
          autoRetried.current = false;
          if (handlers.current.onToken) handlers.current.onToken(data.token);
          break;
        case "pxcaptcha:expired":
          if (handlers.current.onToken) handlers.current.onToken(null);
          break;
        case "pxcaptcha:error":
          if (handlers.current.onToken) handlers.current.onToken(null);
          fail(data.code);
          break;
        case "pxcaptcha:resize": {
          setStatus(prev => (prev === "error" ? prev : "ready"));
          const h = Number(data.height);
          if (h > 0) setHeight(Math.min(Math.ceil(h), MAX_HEIGHT));
          break;
        }
        default:
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [config, action]);

  // Cada vez que el padre cambia resetKey (después de cada intento) se pide un
  // token nuevo: el anterior ya se consumió.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const frame = iframeRef.current;
    if (config && frame && frame.contentWindow)
      frame.contentWindow.postMessage(
        { type: "pxcaptcha:reset" },
        config.origin
      );
  }, [resetKey]);

  if (!config) return null;
  return (
    <>
      {status === "error" ? (
        <Box pb={1}>
          <Alert
            severity="warning"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  autoRetried.current = false;
                  setAttempt(n => n + 1);
                }}
              >
                <FormattedMessage id="login.captcha.retry" />
              </Button>
            }
          >
            <FormattedMessage id="login.captcha.error" />
            {canContinue ? (
              <>
                {" "}
                <FormattedMessage id="login.captcha.continue" />
              </>
            ) : null}
          </Alert>
        </Box>
      ) : null}
      <iframe
        key={attempt}
        ref={iframeRef}
        title={intl.formatMessage({ id: "login.captcha.title" })}
        src={buildCaptchaSrc(config, { action, pos, lng })}
        style={{
          width: "100%",
          height: status === "error" ? 0 : height,
          border: 0,
          display: "block"
        }}
        scrolling="no"
      />
    </>
  );
};

export default CaptchaFrame;
